import { useCallback, useEffect, useRef, useState } from 'react'

import { useAudioContext } from './AudioProvider'

export const useAudio = (props?: { src?: string }) => {
  const { currentAudio, setCurrentAudio } = useAudioContext()
  const { src } = props || {}

  const [blob, setBlob] = useState<Blob | null>(null)

  const [currentProgress, setCurrentProgress] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [volume, setVolume] = useState(1)

  const audioRef = useRef<HTMLAudioElement | null>(new Audio(src))

  const handleProgressChange = useCallback(() => {
    if (audioRef.current) {
      const { currentTime } = audioRef.current

      setCurrentProgress(Number(currentTime.toFixed(0)))
    }
  }, [])

  const handlePlayChange = useCallback(() => {
    if (audioRef.current) {
      setIsPlaying(!audioRef.current.paused)
    }
  }, [])

  const play = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play()
      setIsPlaying(true)
      setCurrentAudio(audioRef.current)
    }
  }, [setCurrentAudio])

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    }
  }, [])

  const handleVolumeChange = useCallback((value: number) => {
    if (audioRef.current) {
      audioRef.current.volume = value
      setVolume(value)
    }
  }, [])

  const updateProgress = useCallback((value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value
      setCurrentProgress(value)
    }
  }, [])

  const initSrc = useCallback(
    async (src: string) => {
      if (audioRef.current) {
        audioRef.current.src = src

        setIsLoading(true)
        const response = await fetch(audioRef.current.src)
        const blob = await response.blob()

        setIsLoading(false)
        setIsLoaded(true)
        setBlob(blob)
        setCurrentAudio(audioRef.current)

        audioRef.current.load()
        play()
      }
    },
    [play, setCurrentAudio],
  )

  useEffect(() => {
    audioRef.current?.addEventListener('timeupdate', handleProgressChange)

    return () =>
      // eslint-disable-next-line react-hooks/exhaustive-deps
      audioRef.current?.removeEventListener('timeupdate', handleProgressChange)
  }, [handleProgressChange])

  useEffect(() => {
    audioRef.current?.addEventListener('play', handlePlayChange)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => audioRef.current?.removeEventListener('play', handlePlayChange)
  }, [handlePlayChange])

  useEffect(() => {
    audioRef.current?.addEventListener('ended', () => {
      setIsPlaying(false)
      setCurrentProgress(0)
    })
  }, [setBlob])

  useEffect(() => {
    if (currentAudio !== audioRef.current) {
      pause()
    }
  }, [currentAudio, pause])

  useEffect(
    () => () => {
      pause()
      setCurrentAudio(null)
    },
    [pause, setCurrentAudio],
  )

  return {
    initSrc,
    audio: audioRef,
    currentProgress,
    isPlaying,
    isLoaded,
    isLoading,
    handleVolumeChange,
    volume,
    play,
    pause,
    updateProgress,
    blob,
  }
}
